// src/components/Dashboard/DashboardMain.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchAnalysisHistory } from '../../redux/slices/analysisSlice';
import { toast } from 'react-toastify';
import PropertyDetailModal from '../Modal/PropertyDetailModal';
import MapModal from '../Modal/MapModal';
import useFeatureAccess from '../../hooks/useFeatureAccess';
import {
  Search,
  MapPin,
  Loader2,
  AlertCircle,
  Mountain,
  Ruler,
  TrendingUp,
  FileCheck,
  Clock,
  Map,
  Building2,
  AlertTriangle,
  Activity,
  Target,
  ExternalLink,
  Info,
  Eye
} from 'lucide-react';
import RegionalLineChart from './RegionalLineChart';
import CityAnalysisSection from './CityAnalysisSection';
import { AnimatePresence } from 'framer-motion';
import {
  detectJurisdiction,
  getZoningMetric,
  getJurisdictionStyle
} from '../Modal/MapModal/utils/jurisdictionHelpers';
import { calculateFeasibilityScore, getScoreColor } from '../../utils/opportunityAnalysis';
import { setCurrentAnalysis } from '../../redux/slices/analysisSlice';


const DashboardMain = ({ onNewSearch, onNavigateToList }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPro } = useFeatureAccess();
  const { analysisHistory, totalAnalyses, isLoadingHistory, error } = useSelector(
    (state) => state.analysis
  );

  // Modal states
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  // Regional Footprint Selection State
  const [selectedCounty, setSelectedCounty] = useState(null);

  console.log("DashboardMain render - analysisHistory:", analysisHistory);

  useEffect(() => {
    dispatch(fetchAnalysisHistory({ limit: 10000, offset: 0 }));
  }, [dispatch]);

  // Opportunity Scoring Logic
  // Opportunity Scoring Logic - Moved to shared utility

  const FeasibilityIndicator = ({ score }) => {
    const color = getScoreColor(score);
    const colorClass = color === 'emerald' ? 'text-emerald-500 border-emerald-100' : color === 'amber' ? 'text-amber-500 border-amber-100' : 'text-rose-500 border-rose-100';
    
    return (
      <div className={`w-10 h-10 rounded-full flex flex-col items-center justify-center border-2 bg-white shadow-sm flex-shrink-0 ${colorClass}`}>
        <span className="text-[10px] font-black leading-none">{score}</span>
        <span className="text-[6px] font-bold uppercase tracking-tighter mt-0.5 opacity-70">Opp.</span>
      </div>
    );
  };


  // Modal handlers
  const handleViewDetails = (analysis) => {
    dispatch(setCurrentAnalysis({ data: analysis }));
    onNavigateToList('search'); // Note: Reusing onNavigateToList for view switching if applicable
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAnalysis(null);
  };

  const handleViewMap = (analysis) => {
    setSelectedAnalysis(analysis);
    setIsMapModalOpen(true);
  };

  const handleCloseMapModal = () => {
    setIsMapModalOpen(false);
    setSelectedAnalysis(null);
  };

  if (isLoadingHistory) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Get recent properties (top 6)
  const recentProperties = analysisHistory.slice(0, 6);

  // Calculate comprehensive insights
  const cdpRequiredCount = analysisHistory.filter(item => item.requires_cdp).length;
  const steepSlopeCount = analysisHistory.filter(item => item.avg_slope_percent_LAH > 25).length;
  const favorableCount = analysisHistory.filter(item =>
    item.avg_slope_percent_LAH <= 15 && !item.requires_cdp
  ).length;

  const avgArea = analysisHistory.length > 0
    ? (analysisHistory.reduce((sum, item) => sum + (item.parcel_area_acres || 0), 0) / analysisHistory.length).toFixed(2)
    : 0;
  const avgSlope = analysisHistory.length > 0
    ? (analysisHistory.reduce((sum, item) => sum + (item.avg_slope_percent_LAH || 0), 0) / analysisHistory.length).toFixed(1)
    : 0;
  const avgMDA = analysisHistory.length > 0
    ? (analysisHistory.reduce((sum, item) => sum + (item.mda_ft2 || 0), 0) / analysisHistory.length / 1000).toFixed(1)
    : 0;



  return (
    <>
      <div className="space-y-6">



        {/* Enhanced Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-blue-500 rounded-xl p-3 shadow-sm">
                <Map className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-900">{totalAnalyses || 0}</p>
                <p className="text-xs text-slate-500 font-medium">Properties</p>
              </div>
            </div>
            <p className="text-sm font-semibold text-slate-700">Total Analyzed</p>
            <div className="flex items-center space-x-1 mt-1">
              <p className="text-xs text-slate-500">Count of all analyses</p>
              <Info className="w-3 h-3 text-slate-400" title="Formula: Length of analysisHistory array" />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-green-500 rounded-xl p-3 shadow-sm">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-900">{favorableCount}</p>
                <p className="text-xs text-slate-500 font-medium">Properties</p>
              </div>
            </div>
            <p className="text-sm font-semibold text-slate-700">Development Ready</p>
            <div className="flex items-center space-x-1 mt-1">
              <p className="text-xs text-slate-500">Slope ≤15% AND No CDP</p>
              <Info
                className="w-3 h-3 text-slate-400"
                title="Formula: Filter where avg_slope_percent_LAH ≤ 15 && !requires_cdp. Count matches."
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-purple-500 rounded-xl p-3 shadow-sm">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-900">{avgMDA}k</p>
                <p className="text-xs text-slate-500 font-medium">sq ft</p>
              </div>
            </div>
            <p className="text-sm font-semibold text-slate-700">Avg Build Area</p>
            <div className="flex items-center space-x-1 mt-1">
              <p className="text-xs text-slate-500">Mean MDA (mda_ft2 / 1000)</p>
              <Info
                className="w-3 h-3 text-slate-400"
                title="Formula: SUM(mda_ft2 for all items) / totalAnalyses / 1000. Rounded to 1 decimal."
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-orange-500 rounded-xl p-3 shadow-sm">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-900">{cdpRequiredCount}</p>
                <p className="text-xs text-slate-500 font-medium">Properties</p>
              </div>
            </div>
            <p className="text-sm font-semibold text-slate-700">CDP Required</p>
            <div className="flex items-center space-x-1 mt-1">
              <p className="text-xs text-slate-500">requires_cdp = true</p>
              <Info
                className="w-3 h-3 text-slate-400"
                title="Formula: Filter where requires_cdp === true. Count matches."
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <AnimatePresence mode="wait">
            {!selectedCounty ? (
              <RegionalLineChart
                analysisHistory={analysisHistory}
                onSelectCounty={setSelectedCounty}
                selectedCounty={selectedCounty}
              />
            ) : (
              <CityAnalysisSection
                selectedCounty={selectedCounty}
                analysisHistory={analysisHistory}
                onBack={() => setSelectedCounty(null)}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Secondary Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-5 border border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Ruler className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-700">Avg Parcel Size</p>
                  <p className="text-xs text-slate-500">Mean parcel_area_acres</p>
                </div>
              </div>
              <p className="text-xl font-bold text-slate-900">{avgArea} <span className="text-sm text-slate-500">ac</span></p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-5 border border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Mountain className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-700">Avg Slope Grade</p>
                  <p className="text-xs text-slate-500">Mean avg_slope_percent_LAH</p>
                </div>
              </div>
              <p className="text-xl font-bold text-slate-900">{avgSlope}<span className="text-sm text-slate-500">%</span></p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-5 border border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Activity className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="text-xs text-slate-500">Slope {'>'} 25%</p>
                </div>
              </div>
              <p className="text-xl font-bold text-slate-900">{steepSlopeCount} <span className="text-sm text-slate-500">sites</span></p>
            </div>
          </div>
        </div>

        {/* Recent Properties Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200">
          <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Recent Properties</h2>
              <p className="text-sm text-slate-600 mt-0.5">Latest property analyses</p>
            </div>
            {recentProperties.length > 0 && (
              <button
                onClick={() => onNavigateToList()}
                className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center space-x-1 hover:bg-blue-50 px-4 py-2 rounded-lg transition-colors"
              >
                <span>View All</span>
                <ExternalLink className="w-4 h-4" />
              </button>
            )}
          </div>

          {error ? (
            <div className="px-6 py-12 text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <p className="text-red-600 font-semibold mb-1">Failed to load properties</p>
              <p className="text-slate-600 text-sm">{error}</p>
            </div>
          ) : recentProperties.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="bg-slate-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Map className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">No properties yet</h3>
              <p className="text-slate-600 text-sm mb-6">Start analyzing properties to see them here</p>
              <button
                onClick={() => onNewSearch()}
                className="bg-slate-900 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-slate-800 transition-colors inline-flex items-center space-x-2"
              >
                <Search className="w-4 h-4" />
                <span>Analyze Your First Property</span>
              </button>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {recentProperties.map((property) => (
                <div
                  key={property.id}
                  onClick={() => handleViewDetails(property)}
                  className="group p-6 hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-6">
                    {/* Property Info */}
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      <FeasibilityIndicator score={calculateFeasibilityScore(property)} />
                      <div className="bg-slate-900 rounded-lg p-2.5 flex-shrink-0 shadow-lg">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors truncate">
                          {property.address || 'N/A'}
                        </h3>
                        <div className="flex items-center space-x-3 mt-1">
                          <span className="text-sm text-slate-600 font-mono">APN: {property.apn || 'N/A'}</span>
                          <span className="text-slate-300">•</span>
                          <div className="flex items-center space-x-1 text-sm text-slate-500">
                            <Clock className="w-3.5 h-3.5" />
                            <span>
                              {new Date(property.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Metrics Grid - Rigid 6-column layout */}
                    <div className="hidden lg:grid grid-cols-[repeat(6,75px)] gap-3 min-w-[500px]">
                      <div className="flex flex-col items-center justify-between text-center">
                        <p className="text-xs text-slate-500 font-medium mb-1">Area</p>
                        <div className="flex-1 flex flex-col items-center justify-center">
                          <p className="text-lg font-bold text-slate-900 leading-none">
                            {property.parcel_area_acres ? property.parcel_area_acres.toFixed(2) : 'N/A'}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">acres</p>
                        </div>
                      </div>

                      <div className="flex flex-col items-center justify-between text-center">
                        <p className="text-xs text-slate-500 font-medium mb-1">Slope</p>
                        <div className="flex-1 flex flex-col items-center justify-center">
                          <p className={`text-lg font-bold leading-none ${property.avg_slope_percent_LAH > 25 ? 'text-red-600' :
                            property.avg_slope_percent_LAH > 15 ? 'text-orange-600' :
                              'text-green-600'
                            }`}>
                            {property.avg_slope_percent_LAH != null ? property.avg_slope_percent_LAH.toFixed(1) : 'N/A'}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">percent</p>
                        </div>
                      </div>

                      <div className="flex flex-col items-center justify-between text-center">
                        <p className="text-xs text-slate-500 font-medium mb-1 truncate w-full" title="Zoning Metric">Zoning Metric</p>
                        <div className="flex-1 flex flex-col items-center justify-center">
                          {(() => {
                            const jur = detectJurisdiction(property.address);
                            const slope = property.avg_slope_percent_LAH || 0;
                            const acres = property.parcel_area_acres || 0;
                            const calcResult = getZoningMetric(jur, slope, acres, property.zone_code || "R1");

                            if (calcResult) {
                              return (
                                <>
                                  <div className="flex items-center justify-center space-x-1 mb-0.5">
                                    <span className="text-lg font-bold text-slate-900 leading-none">{calcResult.convertedValue.toFixed(2)}</span>
                                    <span
                                      className="text-[9px] font-bold px-1 py-0.5 rounded"
                                      style={{
                                        backgroundColor: getJurisdictionStyle(jur).bg,
                                        color: getJurisdictionStyle(jur).color,
                                        border: getJurisdictionStyle(jur).border
                                      }}
                                      title={calcResult.formulaTooltip}
                                    >
                                      {calcResult.formulaLabel}
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-500">acres</p>
                                </>
                              );
                            }
                            return <p className="text-lg font-bold text-slate-400">—</p>;
                          })()}
                        </div>
                      </div>

                      <div className="flex flex-col items-center justify-between text-center">
                        <p className="text-xs text-slate-500 font-medium mb-1">MDA</p>
                        <div className="flex-1 flex flex-col items-center justify-center">
                          <p className="text-lg font-bold text-slate-900 leading-none">
                            {property.mda_ft2 ? (property.mda_ft2 / 1000).toFixed(1) : 'N/A'}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">k ft²</p>
                        </div>
                      </div>

                      <div className="flex flex-col items-center justify-between text-center">
                        <p className="text-xs text-slate-500 font-medium mb-1">MFA</p>
                        <div className="flex-1 flex flex-col items-center justify-center">
                          <p className="text-lg font-bold text-slate-900 leading-none">
                            {property.mfa_ft2 ? (property.mfa_ft2 / 1000).toFixed(1) : 'N/A'}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">k ft²</p>
                        </div>
                      </div>

                      <div className="flex flex-col items-center justify-between text-center">
                        <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1">Opportunity</p>
                        <div className="flex-1 flex flex-col items-center justify-center">
                          {(() => {
                            const score = calculateFeasibilityScore(property);
                            const colorClass = getScoreColor(score);
                            return (
                              <div className={`px-2.5 py-1 rounded-lg font-black text-[11px] uppercase border shadow-sm ${
                                colorClass === 'emerald' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                colorClass === 'amber' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                'bg-rose-50 text-rose-700 border-rose-100'
                              }`}>
                                {score}%
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Status & Action */}
                    <div className="flex items-center space-x-4 flex-shrink-0">
                      <span
                        className={`px-3 py-1.5 rounded-lg font-semibold text-xs ${property.requires_cdp
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                          }`}
                      >
                        {property.requires_cdp ? 'CDP' : 'Clear'}
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (hasPro) {
                              handleViewMap(property);
                            } else {
                              toast.info(
                                <div className="flex flex-col gap-2">
                                  <span className="font-bold">Pro Feature</span>
                                  <span>Upgrade to Professional plan to view interactive maps.</span>
                                  <button
                                    onClick={() => navigate('/#subscription-plans')}
                                    className="mt-2 bg-white text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold self-start hover:bg-blue-50"
                                  >
                                    View Plans
                                  </button>
                                </div>,
                                { autoClose: 5000 }
                              );
                            }
                          }}
                          className={`group/btn p-2.5 rounded-xl transition-all transform ${hasPro
                            ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg hover:scale-110"
                            : "bg-slate-100 text-slate-400 hover:bg-slate-200 cursor-pointer"
                            }`}
                          title={hasPro ? "View on Map with Contours" : "Upgrade to view Map"}
                        >
                          <Map className="w-4 h-4" />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(property);
                          }}
                          className="group/btn bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-2.5 rounded-xl transition-all hover:shadow-lg transform hover:scale-110"
                          title="View Full Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Metrics */}
                  <div className="lg:hidden grid grid-cols-5 gap-2 mt-4 pt-4 border-t border-slate-200">
                    <div className="text-center">
                      <p className="text-[10px] text-slate-500 font-medium mb-0.5">Area</p>
                      <p className="text-sm font-bold text-slate-900">
                        {property.parcel_area_acres ? property.parcel_area_acres.toFixed(2) : 'N/A'}
                      </p>
                    </div>

                    <div className="text-center">
                      <p className="text-[10px] text-slate-500 font-medium mb-0.5">Slope</p>
                      <p className={`text-sm font-bold ${property.avg_slope_percent_LAH > 25 ? 'text-red-600' :
                        property.avg_slope_percent_LAH > 15 ? 'text-orange-600' :
                          'text-green-600'
                        }`}>
                        {property.avg_slope_percent_LAH != null ? property.avg_slope_percent_LAH.toFixed(1) : 'N/A'}
                      </p>
                    </div>

                    <div className="text-center">
                      <p className="text-[10px] text-slate-500 font-medium mb-0.5" title="Zoning Metric">Zoning</p>
                      {(() => {
                        const jur = detectJurisdiction(property.address);
                        const slope = property.avg_slope_percent_LAH || 0;
                        const acres = property.parcel_area_acres || 0;
                        const calcResult = getZoningMetric(jur, slope, acres, property.zone_code || "R1");

                        if (calcResult) {
                          return <p className="text-sm font-bold text-slate-900">{calcResult.convertedValue.toFixed(2)}</p>;
                        }
                        return <p className="text-sm font-bold text-slate-400">—</p>;
                      })()}
                    </div>

                    <div className="text-center">
                      <p className="text-[10px] text-slate-500 font-medium mb-0.5">MDA</p>
                      <p className="text-sm font-bold text-slate-900">
                        {property.mda_ft2 ? (property.mda_ft2 / 1000).toFixed(1) : 'N/A'}
                      </p>
                    </div>

                    <div className="text-center">
                      <p className="text-[10px] text-slate-500 font-medium mb-0.5">MFA</p>
                      <p className="text-sm font-bold text-slate-900">
                        {property.mfa_ft2 ? (property.mfa_ft2 / 1000).toFixed(1) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          {recentProperties.length > 0 && totalAnalyses > 6 && (
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
              <button
                onClick={() => onNavigateToList()}
                className="w-full py-2.5 text-slate-700 hover:text-slate-900 font-semibold text-sm rounded-lg hover:bg-white transition-colors flex items-center justify-center space-x-2 border border-slate-200"
              >
                <span>View All {totalAnalyses} Properties</span>
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Insights Section */}
        {analysisHistory.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center space-x-3 mb-3">
                <div className="bg-blue-600 rounded-lg p-2">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-slate-900">Development Potential</h3>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                {favorableCount > 0
                  ? `${favorableCount} ${favorableCount === 1 ? 'property is' : 'properties are'} development-ready with favorable terrain and no CDP requirements.`
                  : steepSlopeCount > 0
                    ? `${steepSlopeCount} ${steepSlopeCount === 1 ? 'property has' : 'properties have'} steep slopes requiring engineering review.`
                    : 'Portfolio shows mixed development characteristics.'
                }
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
              <div className="flex items-center space-x-3 mb-3">
                <div className="bg-orange-600 rounded-lg p-2">
                  <FileCheck className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-slate-900">Regulatory Overview</h3>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                {cdpRequiredCount > 0
                  ? `${cdpRequiredCount} of ${totalAnalyses} properties (${((cdpRequiredCount / totalAnalyses) * 100).toFixed(0)}%) require Coastal Development Permits.`
                  : 'No coastal development permits required for current portfolio.'
                }
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Property Detail Modal */}
      <PropertyDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        analysis={selectedAnalysis}
      />

      <MapModal
        isOpen={isMapModalOpen}
        onClose={handleCloseMapModal}
        address={selectedAnalysis?.address}
        apn={selectedAnalysis?.apn}
        county={selectedAnalysis?.county}
        sr_wkid={selectedAnalysis?.sr_wkid}
        parcelAreaAcres={selectedAnalysis?.parcel_area_acres}
        mda={selectedAnalysis?.mda_ft2}
        mfa={selectedAnalysis?.mfa_ft2}
        grossBuildingArea={selectedAnalysis?.gross_building_area_ft2}
        livingArea={selectedAnalysis?.living_area_ft2}
        avgSlope={selectedAnalysis?.avg_slope_percent_LAH}
        analysisId={selectedAnalysis?.id}
      />
    </>
  );
};

export default DashboardMain;
